// utils/item-helper.ts

import { IPublicClientApplication } from "@azure/msal-browser";
import { getItemById, getListItems, getSiteAndListId } from "../services/graph-service";
import { useLocation, useNavigate } from "react-router";
import {
  ContractListName,
  GlobalVars,
  PartnerMasterListName,
  PersonalInfoListName,
  SiteListName,
} from "../constants/global-values";

/**
 * Acquire Graph API token and retrieve site/list IDs for a given list.
 */
export async function getTokenAndListInfo(
  instance: IPublicClientApplication,
  listName: string
): Promise<{ token: string; siteId: string; listId: string }> {
  const activeAccount = instance.getActiveAccount();
  if (!activeAccount) throw new Error("No active account found.");

  const result = await instance.acquireTokenSilent({
    scopes: ["https://graph.microsoft.com/.default"],
    account: activeAccount,
  });

  const { siteId, listId } = await getSiteAndListId(result.accessToken, listName);
  return { token: result.accessToken, siteId, listId };
}

/**
 * Fetch an item by ID from a list.
 */
export async function fetchItemById(
  token: string,
  siteId: string,
  listId: string,
  id: string
) {
  const items = await getListItems(token, siteId, listId);
  return items.find((i: { id: string }) => i.id === id);
}

/**
 * Hook to navigate back smartly depending on navigation state.
 */
export function useSmartBack(defaultPath: string = "/") {
  const location = useLocation();
  const navigate = useNavigate();

  const routes: Record<string, string | ((id?: string) => string)> = {
    login: "/",
    list: "/list",
    sitelogin: "/shokai",
    newentry: "/shinki",
    nyuujou: "/nyuujou"
  };

  const goBack = () => {
    const from = location.state?.from as keyof typeof routes | "editshokai" | undefined;

    if (from && routes[from]) {
      const route = routes[from];
      if (typeof route === "function") {
        const userLeaderId = GlobalVars.userid;
        navigate(route(userLeaderId), { state: { from: "sitelogin" } });
      } else {
        navigate(route);
      }
    } else {
      navigate(-1);
    }

  };

  return goBack;
}

/**
 * Format a date as YYYY/MM/DD.
 */
export const formatDate = (date?: Date): string =>
  date ? `${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")}` : "";

/**
 * Convert a date to Japanese format (YYYY年MM月DD日).
 */
export const japaneseDateConverter = (date?: string | Date): string => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return ""; // invalid date safeguard
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
};

/**
 * Validate Japanese phone number (11 digits).
 */
export const validatePhone = (value: string): boolean => /^[0-9]{11}$/.test(value);

/**
 * Format phone number with hyphens.
 */
export const phoneNumFormat = (phno: string): string => {
  const str = phno.toString();
  if (str.length > 3 && str.length <= 7) return `${str.slice(0, 3)}-${str.slice(3)}`;
  if (str.length > 7) return `${str.slice(0, 3)}-${str.slice(3, 7)}-${str.slice(7)}`;
  return str;
};

/**
 * Fetch all detail entities by IDs (contract, site, personal, partner).
 */
export const fetchAllDetails = async (
  instance: IPublicClientApplication,
  ids: { contractId: string; siteId: string; personalId: string; partnerId: string }
) => {
  const listNames = {
    contract: ContractListName,
    site: SiteListName,
    personal: PersonalInfoListName,
    partner: PartnerMasterListName,
  };

  const activeAccount = instance.getActiveAccount();
  if (!activeAccount) throw new Error("No active account found.");

  const result = await instance.acquireTokenSilent({
    scopes: ["https://graph.microsoft.com/.default"],
    account: activeAccount,
  });
  const token = result.accessToken;

  // Get metadata for all lists in parallel
  const [contractMeta, siteMeta, personalMeta, partnerMeta] = await Promise.all([
    getSiteAndListId(token, listNames.contract),
    getSiteAndListId(token, listNames.site),
    getSiteAndListId(token, listNames.personal),
    getSiteAndListId(token, listNames.partner),
  ]);

  // Fetch actual data
  const [contractData, siteData, personalData, partnerData] = await Promise.all([
    getItemById(token, contractMeta.siteId, contractMeta.listId, ids.contractId),
    getItemById(token, siteMeta.siteId, siteMeta.listId, ids.siteId),
    getItemById(token, personalMeta.siteId, personalMeta.listId, ids.personalId),
    getItemById(token, partnerMeta.siteId, partnerMeta.listId, ids.partnerId),
  ]);

  return {
    contractName: contractData?.ClientCompanyName ?? "",
    siteName: siteData?.OnSiteName ?? "",
    leaderName: personalData?.WorkerName ?? "",
    partnerCompany: partnerData?.PartnerName ?? "",
  };
};

/**
 * Convert base64 DataURL to Blob object.
 */
export const dataURLtoBlob = (dataUrl: string): Blob => {
  try {
    const [header, base64] = dataUrl.split(",");
    const mimeMatch = header.match(/:(.*?);/);
    if (!mimeMatch) throw new Error("Invalid dataUrl format");

    const mime = mimeMatch[1];
    const bstr = atob(base64);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  } catch (err) {
    console.error("Failed to convert dataURL to Blob", err);
    throw err;
  }
};
